import type { MouseEvent } from 'react';
import { useCallback, useState } from 'react';

import { type FileRejection, type FileWithPath } from 'react-dropzone';

import type { ErrorMessages } from '../ui/file-input';

const getUniqueFiles = (
  newFiles: FileWithPath[],
  uploadedFiles: FileWithPath[]
) =>
  newFiles.filter(
    (newFile) =>
      !uploadedFiles.some(
        ({ name, size, type }) =>
          name === newFile.name &&
          size === newFile.size &&
          type === newFile.type
      )
  );

export const useFiles = (
  onFilesUpdate: (files: FileWithPath[]) => void,
  errorMessages?: ErrorMessages
) => {
  const [files, setFiles] = useState<FileWithPath[]>([]);
  const [errors, setErrors] = useState<string[]>();

  const addFiles = useCallback(
    (newFiles: FileWithPath[], rejectedFiles: FileRejection[]) => {
      if (!rejectedFiles.length) setErrors([]);
      if (!newFiles.length) return;

      const updatedFiles = [...files, ...getUniqueFiles(newFiles, files)];

      onFilesUpdate(updatedFiles);
      setFiles(updatedFiles);
    },
    [files, onFilesUpdate]
  );

  const removeFile = useCallback(
    (fileToRemove: FileWithPath) => (event: MouseEvent) => {
      event.stopPropagation();
      setErrors([]);

      const updatedFiles = files.filter(
        ({ path, lastModified }) =>
          path !== fileToRemove.path &&
          lastModified !== fileToRemove.lastModified
      );
      setFiles(updatedFiles);
      onFilesUpdate(updatedFiles);
    },
    [files, onFilesUpdate]
  );

  const generateInputErrors = useCallback(
    (rejectedFiles?: FileRejection[]) => {
      const fileErrors: string[] = [];

      rejectedFiles?.forEach(({ errors, file }) => {
        errors.forEach((error) => {
          const getMessage = errorMessages?.[error.code];
          if (!getMessage) return;

          fileErrors.push(getMessage(file.name));
        });
      });

      setErrors(fileErrors);
    },
    [errorMessages]
  );

  return {
    files,
    addFiles,
    removeFile,
    generateInputErrors,
    inputErrors: errors,
  };
};
